/**
 * IncidentsHistoryView - Full-screen view of closed incidents history
 * Follows DefectsHistoryView pattern
 */

import React, { useState, useMemo } from 'react';
import {
  Search,
  Download,
  AlertTriangle
} from 'lucide-react';
import { useIncidentsData } from '../../../../hooks/domain/useIncidentsData';
import { filterTombstoned } from '../../../../sync/tombstone';  // FT-458: hide soft-deleted incidents from history
import { useVessel } from '../../../../contexts/VesselContext';
import Dropdown from '../../../common/Dropdown';
// FT-356b: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import IncidentReportDetailView from './IncidentReportDetailView';
import { formatUserIdentity } from '../../../../utils/userDisplay';

const IncidentsHistoryView = ({ onBack }) => {
  // FT-356b: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('12months');

  // State for detail view
  const [selectedIncident, setSelectedIncident] = useState(null);

  // Data hooks
  // FT-458: history view is display-only — hide soft-deleted (tombstoned)
  // incidents. Incidents have no live delete UI today, but this keeps the read
  // filter consistent + defends the path if a delete is added later.
  const { data: rawIncidents } = useIncidentsData();
  const incidents = filterTombstoned(rawIncidents);
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel();

  // Get closed incidents only
  const closedIncidents = useMemo(() => {
    return (incidents || []).filter(i => i.status === 'closed');
  }, [incidents]);

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
      'high': { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
      'medium': { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
      'low': { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' }
    };
    return colors[severity?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' };
  };

  const getTypeLabel = (type) => {
    const types = {
      'accident': 'Accident',
      'near_miss': 'Near Miss',
      'hazard': 'Hazard',
      'environmental': 'Environmental',
      'equipment_failure': 'Equipment Failure',
      'personnel_injury': 'Personnel Injury',
      'safety': 'Safety'
    };
    return types[type] || type || 'Unknown';
  };

  // Filter by date range
  const filteredByDate = useMemo(() => {
    const now = Date.now();
    const msPerDay = 24 * 60 * 60 * 1000;

    return closedIncidents.filter(incident => {
      const incidentDate = incident.date || incident.createdAt;
      if (!incidentDate) return true;
      const recordDate = new Date(incidentDate).getTime();
      if (isNaN(recordDate)) return true;

      switch (dateRange) {
        case '30days': return (now - recordDate) <= 30 * msPerDay;
        case '3months': return (now - recordDate) <= 90 * msPerDay;
        case '12months': return (now - recordDate) <= 365 * msPerDay;
        default: return true;
      }
    });
  }, [closedIncidents, dateRange]);

  // Filter by severity
  const filteredBySeverity = useMemo(() => {
    if (severityFilter === 'all') return filteredByDate;
    return filteredByDate.filter(i => i.severity === severityFilter);
  }, [filteredByDate, severityFilter]);

  // Filter by type
  const filteredByType = useMemo(() => {
    if (typeFilter === 'all') return filteredBySeverity;
    return filteredBySeverity.filter(i => i.type === typeFilter);
  }, [filteredBySeverity, typeFilter]);

  // Filter by search
  const filteredBySearch = useMemo(() => {
    if (!searchTerm.trim()) return filteredByType;
    const term = searchTerm.toLowerCase();
    return filteredByType.filter(incident => {
      if (incident.description?.toLowerCase().includes(term)) return true;
      if (incident.location?.toLowerCase().includes(term)) return true;
      if (incident.id?.toString().toLowerCase().includes(term)) return true;
      if (incident.rootCause?.toLowerCase().includes(term)) return true;
      return false;
    });
  }, [filteredByType, searchTerm]);

  // Sort by date (newest first)
  const sortedIncidents = useMemo(() => {
    return [...filteredBySearch].sort((a, b) => {
      const dateA = new Date(a.closedAt || a.date || 0).getTime();
      const dateB = new Date(b.closedAt || b.date || 0).getTime();
      return dateB - dateA;
    });
  }, [filteredBySearch]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Date', 'ID', 'Type', 'Severity', 'Location', 'Description', 'Root Cause', 'Closed Date', 'Closed By'];
    // FT-06e: Use formatUserIdentity for "Name (Role)" format in CSV export
    const rows = sortedIncidents.map(i => [
      formatDate(i.date),
      i.id || '',
      getTypeLabel(i.type),
      i.severity || '',
      (i.location || '').replace(/"/g, '""'),
      (i.description || '').replace(/"/g, '""'),
      (i.rootCause || '').replace(/"/g, '""'),
      formatDate(i.closedAt),
      formatUserIdentity(i.closedByName || i.closedBy, i.closedByRole) || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${(vessel?.name || 'vessel').replace(/\s+/g, '-')}-incidents-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Show detail view when an incident is selected
  if (selectedIncident) {
    return (
      <IncidentReportDetailView
        incident={selectedIncident}
        onBack={() => setSelectedIncident(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          {/* Row 1: Title + Export + Close */}
          <div className="flex items-center justify-between mb-4">
            {/* FT-356b: header BackButton removed — bottom bar owns Back. */}
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Incident History</h1>
                <p className="text-sm text-gray-500">{sortedIncidents.length} closed incidents</p>
              </div>
            </div>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export CSV</span>
            </button>
          </div>

          {/* Row 2: Search (full width on mobile) */}
          <div className="relative w-full mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search incidents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          {/* Row 3: Filters - horizontal scroll on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible">
            {/* Severity Filter */}
            <Dropdown
              value={severityFilter}
              onChange={setSeverityFilter}
              width="w-28 sm:w-32 flex-shrink-0"
              options={[
                { value: 'all', label: 'All Severity' },
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' }
              ]}
            />

            {/* Type Filter */}
            <Dropdown
              value={typeFilter}
              onChange={setTypeFilter}
              width="w-32 sm:w-36 flex-shrink-0"
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'accident', label: 'Accident' },
                { value: 'near_miss', label: 'Near Miss' },
                { value: 'hazard', label: 'Hazard' },
                { value: 'environmental', label: 'Environmental' },
                { value: 'equipment_failure', label: 'Equip. Failure' },
                { value: 'personnel_injury', label: 'Personnel Injury' }
              ]}
            />

            {/* Date Range */}
            <Dropdown
              value={dateRange}
              onChange={setDateRange}
              width="w-28 sm:w-32 flex-shrink-0"
              options={[
                { value: '30days', label: 'Last 30 days' },
                { value: '3months', label: 'Last 3 months' },
                { value: '12months', label: 'Last 12 months' },
                { value: 'all', label: 'All time' }
              ]}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {sortedIncidents.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
            <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No closed incidents</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || severityFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Closed incidents will appear here'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Closed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Report</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedIncidents.map((incident) => {
                  const sevColors = getSeverityColor(incident.severity);
                  return (
                    <tr
                      key={incident.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedIncident(incident)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sevColors.bg} ${sevColors.text}`}>
                          {incident.severity?.charAt(0).toUpperCase() + incident.severity?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{getTypeLabel(incident.type)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <span className="block truncate max-w-xs">{incident.description || 'No description'}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(incident.date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{incident.location || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(incident.closedAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <button
                          onClick={() => setSelectedIncident(incident)}
                          className="text-cyan-600 hover:text-cyan-800 text-xs font-medium whitespace-nowrap"
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentsHistoryView;
